"use client";

import { useTranslations } from "next-intl";

import IconImageSparkle from "@ui/central-icons/icons/IconImageSparkle";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { HairlineCardBorder } from "@ui/components/core/HairlineCardBorder";
import type { ResolvedFlowTemplate } from "@/app/_api/flowTemplatesServer";

interface CardFlowTemplateProps {
  item: ResolvedFlowTemplate;
  onSelect: (templateId: string) => void;
}

// Shared by the video and image branches so the preview clip and its poster
// still crop and hover-zoom identically.
const MEDIA_CLASS =
  "tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover tw-transition-transform tw-duration-300 tw-ease-out group-hover:tw-scale-105";

export function CardFlowTemplate({ item, onSelect }: CardFlowTemplateProps) {
  const t = useTranslations("ClientKeys.flow_template_gallery");
  const name = item.flowTemplate.name ?? t("modal_title_fallback");

  return (
    <button
      type='button'
      onClick={() => onSelect(item.id)}
      className='tw-group tw-relative tw-isolate tw-flex tw-w-full tw-cursor-pointer tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-cream tw-text-left tw-outline-none tw-transition-[background-color,box-shadow] tw-duration-300 tw-ease-out hover:tw-bg-white hover:tw-shadow-[0_0_0_1px_rgba(0,0,0,0.07),_0_2px_4px_0_rgba(0,0,0,0.04)] focus-visible:tw-outline-focus'
    >
      <div className='tw-relative tw-aspect-[4/3] tw-w-full tw-overflow-hidden tw-bg-gray-100'>
        {item.flowTemplate.previewVideoUrl ? (
          <video
            src={item.flowTemplate.previewVideoUrl}
            // Static still shows while the clip buffers and if autoplay is blocked.
            poster={item.flowTemplate.thumbnailUrl ?? undefined}
            autoPlay
            loop
            muted
            playsInline
            // Muted+playsInline are required for mobile autoplay; aria-hidden as the
            // card's heading already names the template.
            aria-hidden
            className={MEDIA_CLASS}
          />
        ) : item.flowTemplate.thumbnailUrl ? (
          // eslint-disable-next-line @next/next/no-img-element -- xi-hosted (possibly signed) URL; next/image would require host allowlisting and re-signing
          <img src={item.flowTemplate.thumbnailUrl} alt='' className={MEDIA_CLASS} />
        ) : (
          <div className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-text-gray-400'>
            <IconImageSparkle className={`${iconStrokeFix} tw-size-8`} />
          </div>
        )}
      </div>

      <div className='tw-flex tw-flex-1 tw-flex-col tw-p-5'>
        <h3 className='tw-type-lg tw-text-black tw-flex'>{name}</h3>
        {item.flowTemplate.description && (
          <p className='tw-flex tw-type-xs tw-line-clamp-2 tw-text-gray-500'>{item.flowTemplate.description}</p>
        )}
      </div>

      <HairlineCardBorder roundedClassName='tw-rounded-2xl' />
    </button>
  );
}
